/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Shared severity → icon/colour mapping (SPEC-recent-alerts-summary + SPEC-notification-bell).
 *
 * Single source of truth so the dashboard **Recent Alerts** panel and the header
 * **notification bell** render S0–S3 with the same icon, accent colour, and label.
 * Icons are the ones shipped in `AlertsPanel` (AlertCircle / ShieldAlert / AlertTriangle / Info).
 * Row background and heading emphasis are deliberately NOT part of this map — the panel keeps that
 * decision local (only critical rows are tinted; high-severity headings are red) so consumers don't
 * inherit per-severity backgrounds that the current design intentionally omits.
 */

import { AlertCircle, AlertTriangle, Info, ShieldAlert } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type DisplaySeverity = "critical" | "high" | "medium" | "low";

/** S0 = most critical → S3 = lowest. Unknown/missing → the calm `low` default. */
export function mapSeverity(severity: string | null | undefined): DisplaySeverity {
  switch (severity) {
    case "S0":
      return "critical";
    case "S1":
      return "high";
    case "S2":
      return "medium";
    case "S3":
      return "low";
    default:
      return "low";
  }
}

export interface SeverityStyle {
  /** lucide icon component (shipped set) */
  icon: LucideIcon;
  /** icon colour class */
  iconClass: string;
  /** human label for aria / non-colour cues */
  label: string;
}

// The casts these lines used to carry are gone: they dated from the per-icon subpath imports,
// which shipped no types. lucide-react 1.x removed those subpaths and the repo moved to named
// imports from the package root, which ARE typed -- so the assertions stopped changing the type
// and typescript-eslint now flags them.
const STYLES: Record<DisplaySeverity, SeverityStyle> = {
  critical: { icon: AlertCircle, iconClass: "text-red-500", label: "Critical" },
  high: { icon: ShieldAlert, iconClass: "text-amber-500", label: "High" },
  medium: { icon: AlertTriangle, iconClass: "text-yellow-500", label: "Medium" },
  low: { icon: Info, iconClass: "text-blue-500", label: "Low" },
};

/** Resolve the shared style for a raw severity code (S0–S3 or unknown). */
export function severityStyle(severity: string | null | undefined): SeverityStyle {
  return STYLES[mapSeverity(severity)];
}
